import axios from "axios";
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { ITrackableItem, ITrackableItemInput, trackableItemsAtom, trackableItemsLoadingAtom } from "./types";

type UseTrackableItems = [ITrackableItem[], {
    list: (query?: Record<string, any>) => Promise<ITrackableItem[] | null>;
    getById: (id: string) => Promise<ITrackableItem | null>;
    getPaginated: (first?: number, offset?: number, query?: Record<string, any>) => Promise<ITrackableItem[] | null>;
    create: (input: ITrackableItemInput) => Promise<ITrackableItem | null>;
    update: (id: string, input: ITrackableItemInput) => Promise<ITrackableItem | null>;
    remove: (id: string) => Promise<ITrackableItem | null>;
}];

const gqlEndpoint = `${environment.baseUrl}/graphql`;
const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
    const { data } = await axios.post(gqlEndpoint, { query, variables }, { headers: { 'X-Wallmates-Auth': authToken } });
    if (data.errors) throw new Error(data.errors[0]?.message || 'GraphQL error');
    return data.data;
}

export const useTrackableItems = (): UseTrackableItems => {
    const [items, setItems] = useAtom(trackableItemsAtom);
    const setLoading = useSetAtom(trackableItemsLoadingAtom);
    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();

    const list = async (query: Record<string, any> = {}) => {
        try { setLoading(true);
            const q = `query($query: JSON){ getTrackableItems(query: $query){ id vendor_id stock_type_id unit_type_id standard_unit_count default_zones stock_count unit_count cost cost_basis consumption_basis repurchase_threshold sku name notes audit_group_id created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { query });
            setItems(data.getTrackableItems);
            return data.getTrackableItems as ITrackableItem[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); setSnackbar({show:true,snackbarLevel:'error',text:'Failed to fetch trackable items.'}); return null; }
        finally { setLoading(false);} };

    const getById = async (id: string) => {
        try { const q = `query($id: ID!){ getTrackableItemById(id:$id){ id vendor_id stock_type_id unit_type_id standard_unit_count default_zones stock_count unit_count cost cost_basis consumption_basis repurchase_threshold sku name notes audit_group_id created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.getTrackableItemById as ITrackableItem;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const getPaginated = async (first = 20, offset = 0, query: Record<string, any> = {}) => {
        try { const q = `query($first:Int,$offset:Int,$query:JSON){ getPaginatedTrackableItems(first:$first,offset:$offset,query:$query){ id vendor_id stock_type_id unit_type_id standard_unit_count default_zones stock_count unit_count cost cost_basis consumption_basis repurchase_threshold sku name notes audit_group_id created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { first, offset, query });
            return data.getPaginatedTrackableItems as ITrackableItem[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const create = async (input: ITrackableItemInput) => {
        try { const q = `mutation($input:TrackableItemInput!){ createTrackableItem(input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { input });
            return data.createTrackableItem as ITrackableItem;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const update = async (id: string, input: ITrackableItemInput) => {
        try { const q = `mutation($id:ID!,$input:TrackableItemInput!){ updateTrackableItem(id:$id,input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { id, input });
            return data.updateTrackableItem as ITrackableItem;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const remove = async (id: string) => {
        try { const q = `mutation($id:ID!){ deleteTrackableItem(id:$id){ id } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.deleteTrackableItem as ITrackableItem;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    return [items, { list, getById, getPaginated, create, update, remove }];
}


